import { useEffect, useState } from "react";
import { Col, Input, Layout, Row, Space, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { PeriodSelector } from "./components/PeriodSelector";
import { useUserState } from "../../../../common/UserContext";
import { handleError } from "../../../../common/ErrorHandling";
import { GenericSegmented } from "../../../../common/components/GenericSegmented";
import { getDashboardData } from "../../../../common/api/DashboardClientV2";
import { DashboardData } from "../../../../types/DashboardTypes";
import { Banner } from "./components/Banner";
import { DealCard } from "./components/DealCard";
import { RFXCard } from "./components/RFXCard";
import classes from "./NegotiationsView.module.less";

const getQuarter = (date: Date) => Math.ceil((new Date(date).getMonth() + 1) / 3);
const getYear = (date: Date) => new Date(date).getFullYear();

function filterOrderForms(orderForms: DashboardData["orderForms"], selectedYear: number | null) {
    const forms =
        selectedYear === null
            ? orderForms
            : orderForms.filter(
                  (o) => o.orderEndDate && getYear(new Date(o.orderEndDate)) === selectedYear
              );
    return [...forms].sort((a, b) => Number(b.decrease || 0) - Number(a.decrease || 0));
}

function filterTactics(tactics: DashboardData["tactics"], selectedYear: number | null) {
    const filtered =
        selectedYear === null
            ? tactics
            : tactics.filter(
                  (t) =>
                      t.recordCreationDate &&
                      getYear(new Date(t.recordCreationDate)) === selectedYear
              );
    return [...filtered].sort(
        (a, b) =>
            new Date(a.recordCreationDate).getTime() - new Date(b.recordCreationDate).getTime()
    );
}

export const NegotiationsView = () => {
    const { me } = useUserState();
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [selectedQuarters, setSelectedQuarters] = useState<number[]>([1]);
    const [checkedQuarters, setCheckedQuarters] = useState<number[]>([]);
    const emptyDashboard: DashboardData = {
        orderForms: [],
        tactics: [],
        metrics: [],
        intelligence: {
            totalSpend: 0,
            orderCount: 0,
            rfxCount: 0,
            avgOrderScore: 0,
            avgOptimizedScore: 0,
            totalSavings: 0,
            totalAvgScore: 0,
        },
        yearly: {},
    };
    const [dashboard, setDashboard] = useState<DashboardData>(emptyDashboard);
    const [listTab, setListTab] = useState<"Deals" | "RFXs">("Deals");
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    const ALL_YEARS = -1;
    const yearOptions = [
        { label: "All", value: ALL_YEARS },
        ...Object.keys(dashboard.yearly)
            .map(Number)
            .sort((a, b) => a - b)
            .map((y) => ({ label: String(y), value: y })),
    ];

    const yearData = selectedYear !== null ? dashboard.yearly[String(selectedYear)] : null;

    const filteredOrderForms = filterOrderForms(dashboard.orderForms, selectedYear);

    const filteredTactics = filterTactics(dashboard.tactics, selectedYear);

    const filteredIntelligence = yearData?.intelligence ?? dashboard.intelligence;

    const quarterOptions = yearData?.quarters ?? dashboard.metrics;

    const tacticQuarterOptions = quarterOptions.map((m) => ({
        quarter: m.quarter,
        contracts: m.tactics,
        savings: 0,
    }));

    const yearMetric = quarterOptions.reduce(
        (pre, cur) => ({
            contracts: pre.contracts + cur.contracts,
            savings: pre.savings + cur.savings,
        }),
        { contracts: 0, savings: 0 }
    );

    // When "All" is selected, show all filtered data; when a year is selected, also filter by quarter
    const searchLower = search.toLowerCase();
    const displayedOrders = (
        selectedYear === null
            ? filteredOrderForms
            : filteredOrderForms.filter((o) =>
                  selectedQuarters.includes(getQuarter(new Date(o.orderEndDate!)))
              )
    ).filter(
        (o) =>
            !search ||
            (o.vendor?.name || o.vendorKey || "")
                .toLowerCase()
                .split(/\s+/)
                .some((w: string) => w.startsWith(searchLower))
    );
    const displayedTactics = (
        selectedYear === null
            ? filteredTactics
            : filteredTactics.filter((t) =>
                  selectedQuarters.includes(getQuarter(new Date(t.recordCreationDate)))
              )
    ).filter(
        (t) =>
            !search ||
            (t.name || "")
                .toLowerCase()
                .split(/\s+/)
                .some((w: string) => w.startsWith(searchLower))
    );

    const handleQuarterSelect = (quarter: number) => {
        setSelectedQuarters([quarter]);
        setCheckedQuarters([]);
    };

    const handleQuarterToggle = (quarter: number) => {
        setCheckedQuarters((prev) => {
            // If no checkboxes yet, seed from current selection
            const base = prev.length === 0 ? [...selectedQuarters] : prev;
            const next = base.includes(quarter)
                ? base.filter((q) => q !== quarter)
                : [...base, quarter].sort();
            setSelectedQuarters(next.length > 0 ? next : [quarter]);
            return next;
        });
    };

    const handleYearChange = (year: number) => {
        setSelectedYear(year === ALL_YEARS ? null : year);
        setSelectedQuarters([1]);
        setCheckedQuarters([]);
    };

    // Fetch all data once on mount
    useEffect(() => {
        setLoading(true);
        getDashboardData()
            .then((data) => {
                setDashboard(data);
            })
            .catch(handleError)
            .finally(() => setLoading(false));
    }, []);

    return (
        <Layout className={classes.layout}>
            <Layout.Header className={classes.header}>
                <h2 style={{ margin: 0 }}>Negotiation Intelligence</h2>
                <p style={{ margin: 0, color: "rgba(0, 0, 0, 0.45)" }}>
                    Unlock smarter deal making with data-driven negotiation
                    intelligence.
                </p>
            </Layout.Header>

            <Layout.Content className={classes.content}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Banner
                        name={me.firstName}
                        savings={yearMetric.savings}
                        contracts={yearMetric.contracts}
                        year={selectedYear}
                        intelligence={filteredIntelligence}
                    />
                    <Row>
                        <Col>
                            <h3 style={{ margin: 0 }}>Top savings opportunities</h3>
                            <p style={{ margin: 0, color: "rgba(0, 0, 0, 0.45)" }}>
                                View estimated savings by quarter and year.
                            </p>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={3}>
                            <PeriodSelector
                                year={selectedYear ?? ALL_YEARS}
                                yearOptions={yearOptions}
                                onYearChange={handleYearChange}
                                selectedQuarters={selectedQuarters}
                                checkedQuarters={checkedQuarters}
                                hideQuarters={selectedYear === null}
                                quarterOptions={
                                    listTab === "Deals"
                                        ? quarterOptions
                                        : tacticQuarterOptions.map((o) => ({
                                                ...o,
                                                contractsLabel: "RFx created",
                                                type: "rfx" as const,
                                            }))
                                }
                                onQuarterSelect={handleQuarterSelect}
                                onQuarterToggle={handleQuarterToggle}
                            />
                        </Col>
                        <Col span={21}>
                            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
                                <Col>
                                    <GenericSegmented
                                        options={[
                                            { label: "Deals", value: "Deals" },
                                            { label: "RFXs", value: "RFXs" },
                                        ]}
                                        value={listTab}
                                        onChange={(value) =>
                                            setListTab(value as "Deals" | "RFXs")
                                        }
                                    />
                                </Col>
                                <Col>
                                    <Input
                                        className={classes.search}
                                        placeholder="Search"
                                        prefix={
                                            <SearchOutlined style={{ color: "#1890FF" }} />
                                        }
                                        allowClear
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </Col>
                            </Row>
                            {loading ? (
                                <div style={{ textAlign: "center", padding: 48 }}>
                                    <Spin size="large" />
                                </div>
                            ) : listTab === "Deals" ? (
                                displayedOrders.map((deal) => (
                                    <DealCard key={deal.id} deal={deal} />
                                ))
                            ) : (
                                displayedTactics.map((tactic) => (
                                    <RFXCard key={tactic.id} tactic={tactic} />
                                ))
                            )}
                        </Col>
                    </Row>
                </Space>
            </Layout.Content>
        </Layout>
    );
};
